import dynamic from 'next/dynamic';
import { Theme } from '@theme/base';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { getShowModalPayload, ModalType, showModal } from '@state/slices/modal/modalSlice';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { CustomerApi } from '@model/iceberg/service/customer/customer-api';
import { log } from '@util/logging';
import { LogType } from '@model/common/logging';
import { OrderBrochureFormValues } from '@model/forms';
import { EndPoint } from '@model/service';
import { CustomerEndpoints } from '@model/iceberg/service/customer/customer-endpoints';
import { GtmBootstrap } from '@model/bootstrap/gtm-bootstrap';
import Cookies, { Cookie } from '@model/common/cookie/cookie';

const DynamicOrderBrochureForm = dynamic(() => import('./OrderBrochureForm').then((e) => e.OrderBrochureForm), {
  ssr: false
});

export interface OnlineBrochureFormContainerProps {
  theme?: Theme;
  testId?: string;
}

const OrderBrochureFormContainerComponent = (props: OnlineBrochureFormContainerProps) => {
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const urlSearchString = window.location.search;
  const searchParams = new URLSearchParams(urlSearchString);
  const marketingCode = (searchParams.get('mcode') || new Cookies().get(Cookie.MARKETING_CODE)) ?? '';

  const getModalErrorMessages = (errors: any) =>
    Object.entries(errors).map(([field, value]: any) => `${value}-${field}`);

  const signUpForNewsletter = async (email: string) => {
    if (executeRecaptcha) {
      const reCaptcha = await executeRecaptcha('contact');
      const utmParams = new GtmBootstrap().getUtmParams();

      try {
        await new CustomerApi().subscribeToNewsletter({
          email,
          reCaptcha,
          ...utmParams
        });

        log({
          text: `[OrderBrochureFormComponent]: User ${email} has beens signed up for the newsletter`,
          logType: LogType.LOG
        });
      } catch (e: any) {
        log({
          text: `[OrderBrochureFormComponent]: Error while tryng to sing up the user ${email} for the newsletter`,
          logType: LogType.ERROR
        });
      }
    }
  };

  const requestOrderBrochure = async (payload: OrderBrochureFormValues, resetForm: () => void) => {
    try {
      await new CustomerApi().orderBrochure(payload);
      dispatch(
        showModal(
          getShowModalPayload(ModalType.FORM_SUBMIT_SUCCESS, { bodyLabel: 'order-brochure__success-modal--body' })
        )
      );
      resetForm();
      log({
        text: `[OrderBrochureFormComponent]: Order a Brochure request sent successfully`,
        logType: LogType.LOG
      });
    } catch (e: any) {
      dispatch(showModal(getShowModalPayload(ModalType.FORM_SUBMIT_ERROR)));
      log({
        text: `[OrderBrochureFormComponent]: Error sending Order a Brochure request ${EndPoint.CUSTOMER}${CustomerEndpoints.ORDER_BROCHURE}: ${e.errorCode} ${e.errorText}`,
        logType: LogType.ERROR
      });
    }
  };

  const handleSubmit = async (formValues: any, errors: Array<string>, resetForm: () => void) => {
    if (!isEmpty(errors)) {
      dispatch(showModal(getShowModalPayload(ModalType.VALIDATION_ERROR, getModalErrorMessages(errors))));
    } else {
      if (formValues.email) {
        await signUpForNewsletter(formValues.email);
      }
      await requestOrderBrochure({ ...formValues, marketingCode }, resetForm);
    }
  };

  return <DynamicOrderBrochureForm {...props} onSubmit={handleSubmit} />;
};

export const OrderBrochureFormContainer = (props: OnlineBrochureFormContainerProps) => {
  return <OrderBrochureFormContainerComponent {...props} />;
};
